import { QueryString } from 'core/utils/queryString/queryString';
import { LoyaltyOffer } from 'gql/autogen/LoyaltyOffer';
import { Pricing } from 'gql/autogen/Pricing';
import { DetailsState } from 'component/page/detail/store/details';
import { BookingRequirement } from 'gql/autogen/BookingRequirement';
import { Detail } from 'gql/autogen/Detail';
import { Content } from 'gql/autogen/Content';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { CancellationType } from 'gql/autogen/CancellationType';
import { CurrencyDisplay, PriceFormatter } from '@agoda/react-handyman';
import { PriceFormatConfiguration } from '@agoda/react-handyman/lib/utils/priceFormatter/types';
import { Description } from 'gql/autogen/Description';
import { Location } from 'gql/autogen/Location';
import { RangeFilterMatrix } from 'gql/autogen/RangeFilterMatrix';
import { FilterCode } from 'gql/autogen/FilterCode';
import { ValueFilterMatrix } from 'gql/autogen/ValueFilterMatrix';
import { DetailsActivityResult } from 'gql/autogen/DetailsActivityResult';
import { SummaryElement } from 'gql/autogen/SummaryElement';
import { Logistic } from 'gql/autogen/Logistic';
import { Optional } from 'core/type/generic';
import { search as locationSearch } from 'core-framework/window/location';
import { BootstrapConfig, Experiments } from 'core/type/BootstrapConfig.types';
import { DisplayTotalPrice } from 'gql/autogen/DisplayTotalPrice';
import { getElementAtIndex, isInPositiveInt32Range } from 'core-framework/util/CommonUtils';
import { PriceCrossedOutRateBehavior, PriceDisplayBehavior, PriceDisplayType } from 'core/type/behaviors.types';
import { PricingSummary } from 'gql/autogen/PricingSummary';
import { isPast, isToday, isValid, parseISO } from 'date-fns';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { RepresentativeInfo } from 'gql/autogen/RepresentativeInfo';
import { Cancellation } from 'gql/autogen/Cancellation';
import { BadgeType } from 'gql/autogen/BadgeType';
import { getLoyaltySearchTypeFromLocalStorage } from 'core/utils/loyaltySearchType';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { Badge } from 'gql/autogen/Badge';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

const EmptyObject = {};
const EmptyValue = '';

export const mapActivityTokenByRepresentativeInfo = (representativeInfo?: RepresentativeInfo): string => {
    return (representativeInfo && representativeInfo.activityToken) || EmptyValue;
};

export const mapActivityIdByRepresentativeInfo = (representativeInfo?: RepresentativeInfo, defaultActivityId = 0): number => {
    return (representativeInfo && representativeInfo.activityId) || defaultActivityId;
};

export const mapSupplierActivityCodeByRepresentativeInfo = (representativeInfo?: RepresentativeInfo): string => {
    return (representativeInfo && representativeInfo.supplierActivityCode) || EmptyValue;
};

export const mapActivityRepresentativeInfo = (activity: ActivityWithContent): Optional<RepresentativeInfo> => {
    return activity.activityRepresentativeInfo ? activity.activityRepresentativeInfo : undefined;
};

export const mapPricingByPricingSummary = (pricingSummary?: PricingSummary): Optional<Pricing> => {
    return pricingSummary && pricingSummary.pricing && pricingSummary.pricing.length > 0 ? pricingSummary.pricing[0] : undefined;
};

export const mapCancellationTypeByCancellation = (cancellation?: Cancellation) => {
    return (cancellation && cancellation.cancellationType) || CancellationType.NON_REFUNDABLE;
};

export const mapCancellationPoliciesByCancellation = (cancellation?: Cancellation) => {
    return cancellation && cancellation.policies;
};

export const mapLoyaltyOfferByPricing = (pricing: Pricing = EmptyObject, priceDisplay = PriceDisplayType.PerBook): LoyaltyOffer => {
    const pricePerBook = (pricing.display && pricing.display.perBook) || (EmptyObject as SummaryElement);
    const perBookOffer = getElementAtIndex(pricePerBook.loyaltyOffers, 0) || EmptyObject;
    if (priceDisplay === PriceDisplayType.PerBook) {
        return perBookOffer;
    }
    const priceElements = (pricing.display && pricing.display.perPax) || ([EmptyObject] as SummaryElement[]);
    const pricePerPax = getElementAtIndex(priceElements, 0);
    return getElementAtIndex(pricePerPax?.loyaltyOffers, 0) || perBookOffer;
};

export const ActivityPriceFormat: PriceFormatConfiguration = {
    isSpaceBetweenSymbolAndCurrency: true,
    currencyDisplay: CurrencyDisplay.Symbol,
    decimalPlaces: 2,
};

export const NoDecimalPriceFormat: PriceFormatConfiguration = {
    isSpaceBetweenSymbolAndCurrency: true,
    currencyDisplay: CurrencyDisplay.Symbol,
    decimalPlaces: 0,
};

export interface PointBreakupProps {
    amount: number;
    corAmount: number;
    payablePrice: number;
    points: number;
    currency: string;
    formattedAmount: string;
    formattedAmountWithoutDecimal: string;
    formattedPoints: string;
    formattedPayablePrice: string;
    formattedInclusivePrice: string;
}

const DefaultPointBreakupByOffer = {
    amount: 0,
    corAmount: 0,
    payablePrice: 0,
    points: 0,
    currency: EmptyValue,
    formattedAmountWithoutDecimal: '0',
    formattedAmount: '0.00',
    formattedPoints: '0',
    formattedPayablePrice: '0.00',
    formattedInclusivePrice: '0.00',
};

export const selectDisplayPricing = (pricing: Optional<Pricing>, priceDisplay = PriceDisplayType.PerBook): Optional<DisplayTotalPrice> => {
    if (priceDisplay === PriceDisplayType.PerBook) {
        const chargeTotal = pricing?.display?.perBook?.total?.allInclusive?.chargeTotal;
        return chargeTotal === undefined ? undefined : pricing?.display?.perBook?.total;
    }
    const perPaxPrices = (pricing?.display?.perPax || ([] as SummaryElement[]))
        .filter((price) => (price?.total?.allInclusive?.chargeTotal || 0) > 0 || (price?.loyaltyOffers?.length || 0) > 0)
        .map((price) => price.total)
        .sort((price1, price2) => (price1?.allInclusive?.chargeTotal || 0) - (price2?.allInclusive?.chargeTotal || 0));

    if (!perPaxPrices || perPaxPrices.length === 0) return undefined;
    return perPaxPrices[0];
};

export const getCrossedOutRate = (corBehavior: PriceCrossedOutRateBehavior, totalPrice?: DisplayTotalPrice): number => {
    const allInclusiveChargeTotal = totalPrice?.allInclusive?.chargeTotal || 0;
    const allInclusiveCrossedOutRate = totalPrice?.allInclusive?.crossedOut || 0;
    return corBehavior === PriceCrossedOutRateBehavior.Supported && allInclusiveCrossedOutRate !== 0 && allInclusiveChargeTotal < allInclusiveCrossedOutRate
        ? allInclusiveCrossedOutRate
        : 0;
};

export const getOfferWithMinPricingSummary = (enrichedOfferOption: EnrichedOfferOption[] = []): EnrichedOfferOption | undefined => {
    const availableOptions = enrichedOfferOption.filter((e) => e.isAvailable);
    const getMinOption = (minPriceOption: EnrichedOfferOption | undefined, currentOption: EnrichedOfferOption | undefined) => {
        const currentPricing = mapPricingByPricingSummary(currentOption?.pricingSummary);
        const minPricing = mapPricingByPricingSummary(minPriceOption?.pricingSummary);
        const currentChargeTotal = currentPricing?.display?.perBook?.total?.allInclusive?.chargeTotal ?? 0;
        const minChargeTotal = minPricing?.display?.perBook?.total?.allInclusive?.chargeTotal ?? Number.MAX_VALUE;
        if (currentChargeTotal < minChargeTotal && currentChargeTotal > 0) {
            return currentOption;
        }
        return minPriceOption;
    };
    const minPriceOption = availableOptions.reduce(getMinOption, undefined);
    return minPriceOption;
};

export const cheapestOfferOptionSort = (a: string | undefined, b: string | undefined) => {
    if (!a || !b) return (a || '').localeCompare(b || '');

    // Try to extract numeric values with units from anywhere in the string
    const numRegexA = a.match(/(\d+(?:\.\d+)?)\s*(MB|GB|KB|[A-Za-z]+)?/i);
    const numRegexB = b.match(/(\d+(?:\.\d+)?)\s*(MB|GB|KB|[A-Za-z]+)?/i);

    // If both strings contain numbers with potentially the same units
    if (numRegexA && numRegexB) {
        const valueA = parseFloat(numRegexA[1]);
        const valueB = parseFloat(numRegexB[1]);
        const unitA = numRegexA[2]?.toLowerCase() || '';
        const unitB = numRegexB[2]?.toLowerCase() || '';

        // If units are the same, compare numeric values
        if (unitA === unitB) {
            return valueA - valueB;
        }

        // Handle special case for data units (KB, MB, GB)
        const dataUnits: Record<string, number> = { kb: 1, mb: 1024, gb: 1024 * 1024 };
        if (unitA in dataUnits && unitB in dataUnits) {
            return valueA * (dataUnits[unitA] || 0) - valueB * (dataUnits[unitB] || 0);
        }
    }

    // Default to alphabetical sorting
    return a.localeCompare(b);
};
export const getCheapestOfferOption = (enrichedOfferOption: EnrichedOfferOption[] = []): EnrichedOfferOption | undefined => {
    const availableOptions = enrichedOfferOption.filter((e) => e.isAvailable);
    const getCheapestOption = (cheapestOption: EnrichedOfferOption | undefined, currentOption: EnrichedOfferOption | undefined) => {
        const currentPricing = mapPricingByPricingSummary(currentOption?.pricingSummary);
        const cheapestPrice = mapPricingByPricingSummary(cheapestOption?.pricingSummary);
        const currentChargeTotal = currentPricing?.display?.perBook?.total?.allInclusive?.chargeTotal ?? 0;
        const cheapestChargeTotal = cheapestPrice?.display?.perBook?.total?.allInclusive?.chargeTotal ?? Number.MAX_VALUE;

        // If prices are different, return the cheaper option
        if (currentChargeTotal < cheapestChargeTotal && currentChargeTotal > 0) {
            return currentOption;
        }
        if (currentChargeTotal > cheapestChargeTotal) {
            return cheapestOption;
        }

        // If prices are equal, sort by specification alphabetically
        const currentSpec = currentOption?.specification?.[0]?.specification || '';
        const cheapestSpec = cheapestOption?.specification?.[0]?.specification || '';
        const ascendingCheapest = cheapestOfferOptionSort(currentSpec, cheapestSpec);

        return ascendingCheapest < 0 ? currentOption : cheapestOption;
    };

    const cheapestOption = availableOptions.reduce(getCheapestOption, undefined);
    return cheapestOption;
};

export const getOfferFromSelectedOfferId = (enrichedOfferOption: EnrichedOfferOption[] = [], selectedOfferId: number | undefined): EnrichedOfferOption | undefined => {
    return enrichedOfferOption.find((e) => e.offerId === selectedOfferId);
};

export const getOfferFromSelectedOptionParameters = (
    enrichedOfferOption: EnrichedOfferOption[] = [],
    selectedOfferId?: number,
    selectedOfferOptionId?: number,
): EnrichedOfferOption | undefined => {
    return enrichedOfferOption.find((e) => e.offerId === selectedOfferId && e.offerOptionId === selectedOfferOptionId);
};

export const getLoyaltyOfferPoints = (offer: LoyaltyOffer) => {
    return getLoyaltySearchTypeFromLocalStorage() === LoyaltySearchType.EARN ? offer.earnOffer?.points : offer.burnOffer?.points;
};

export const getLoyaltyOfferPayableAmt = (offer: LoyaltyOffer) => {
    return offer.burnOffer?.userPayableAmount;
};

export const mapPromocodeBadgesFromPricingSummary = (pricingSummary: PricingSummary | undefined, experiments: Experiments): Badge[] => {
    if (isB(ExperimentId.ACTB_AUTO_APPLIED_PRICE_PEEK, experiments)) {
        const pricing = mapPricingByPricingSummary(pricingSummary);
        return pricing?.display?.perBook?.badges?.promocodes || [];
    }

    return [];
};

export const isPricePeekPromocode = (pricingSummary: PricingSummary | undefined): boolean => {
    if (!pricingSummary) return false;

    const pricing = mapPricingByPricingSummary(pricingSummary);
    const pricePeekBadge = pricing?.display?.perBook?.badges?.promocodes?.filter((pmc) => pmc.badgeType === BadgeType.PRICE_PEEK);
    return !!(pricePeekBadge && pricePeekBadge.length > 0);
};

export const mapPointBreakupByOffer = (
    locale: string,
    pricingSummary?: PricingSummary,
    priceDisplay = PriceDisplayType.PerBook,
    displayBehavior = PriceDisplayBehavior.Price,
    corBehavior = PriceCrossedOutRateBehavior.NotSupported,
): PointBreakupProps => {
    const pricing = mapPricingByPricingSummary(pricingSummary);
    if (!pricing || !pricing.display || !pricing.display.perBook) {
        return DefaultPointBreakupByOffer;
    }

    const totalPrice = selectDisplayPricing(pricing, priceDisplay);
    const loyaltyOffer = mapLoyaltyOfferByPricing(pricing, priceDisplay);
    const loyaltyOfferAmount = getLoyaltyOfferPayableAmt(loyaltyOffer);

    const allInclusiveChargeTotal = totalPrice?.allInclusive?.chargeTotal || 0;
    const amount = allInclusiveChargeTotal;
    const corAmount = getCrossedOutRate(corBehavior, totalPrice);
    const payablePrice = loyaltyOfferAmount || 0;
    const points = Math.floor(getLoyaltyOfferPoints(loyaltyOffer) || 0);

    const currency = (pricing && pricing.currency) || EmptyValue;
    const formattedAmountWithoutDecimal = PriceFormatter.format(amount, locale, { ...NoDecimalPriceFormat, currency });
    const formattedAmount = PriceFormatter.format(amount, locale, { ...ActivityPriceFormat, currency });
    const formattedInclusivePrice = PriceFormatter.format(allInclusiveChargeTotal, locale, { ...ActivityPriceFormat, currency });
    const formattedPayablePrice = PriceFormatter.format(payablePrice, locale, { ...ActivityPriceFormat, currency });
    const formattedPoints = PriceFormatter.format(points, locale);
    return {
        amount,
        corAmount,
        payablePrice,
        points,
        currency,
        formattedAmount,
        formattedAmountWithoutDecimal,
        formattedPoints,
        formattedPayablePrice,
        formattedInclusivePrice,
    };
};

export const mapTotalPriceByOffer = (pricingSummary?: PricingSummary, priceDisplay = PriceDisplayType.PerBook): number => {
    const pricing = mapPricingByPricingSummary(pricingSummary);
    if (!pricing || !pricing.display || !pricing.display.perBook) {
        return 0;
    }

    const totalPrice = selectDisplayPricing(pricing, priceDisplay);
    return totalPrice?.allInclusive?.chargeTotal || 0;
};

export const mapCityIdForSearch = (config: BootstrapConfig): number => {
    // First get from Query string to help in testing
    const search = locationSearch() || EmptyValue;
    const qs = new QueryString(search);
    const queryCityId = qs.get('cityId');
    if (queryCityId) {
        const cityId = parseInt(queryCityId, 10);
        // BE Get Overflow when cityId is more than MAX_INT32. It because GQL can't parse number more than Int32.
        if (isInPositiveInt32Range(cityId)) {
            return cityId;
        }
    }

    const geo = config.geo;
    if (geo && geo.cityId && geo.cityId > 0) {
        return geo.cityId;
    }
    return 0;
};

export const getSelectedActivityFromQueryParams = (queryString: string): number | undefined => {
    const qs = new QueryString(queryString);
    const querySelectedActivity = qs.get(SearchQueryKey.SelectedActivity);
    if (querySelectedActivity) {
        const activityId = parseInt(querySelectedActivity, 10);
        // BE Get Overflow when activityId is more than MAX_INT32. It because GQL can't parse number more than Int32.
        if (isInPositiveInt32Range(activityId)) {
            return activityId;
        }
    }
    return undefined;
};

export const getTravelDateFromQueryParams = (queryString: string): Date | undefined => {
    const queryTravelDate = new QueryString(queryString).get(SearchQueryKey.TravelDate);
    if (queryTravelDate) {
        const date = parseISO(queryTravelDate);
        return isValid(date) && (!isPast(date) || isToday(date)) ? date : undefined;
    }
    return undefined;
};

export const getPaxFromQueryParams = (queryString: string): string | undefined => {
    const queryParams = new QueryString(queryString);
    return queryParams.get(SearchQueryKey.Pax) || undefined;
};

export const getEnrichedOfferIdFromQueryParams = (queryString: string): number | undefined => {
    const queryParams = new QueryString(queryString);
    return Number(queryParams.get(SearchQueryKey.EnrichedOfferId));
};

export const getOfferOptionIdFromQueryParams = (queryString: string): number | undefined => {
    const queryParams = new QueryString(queryString);
    return Number(queryParams.get(SearchQueryKey.OfferOptionId));
};

export const getKeywordQueryParams = (locationString: string): string | undefined => {
    const queryParams = new URLSearchParams(locationString);
    return queryParams.get(SearchQueryKey.Keyword) || undefined;
};

export const getCityIdQueryParams = (locationString: string): string | undefined => {
    const queryParams = new URLSearchParams(locationString);
    return queryParams.get(SearchQueryKey.CityId) || undefined;
};

export const getSelectedActivityQueryParams = (locationString: string): string | undefined => {
    const queryParams = new URLSearchParams(locationString);
    return queryParams.get(SearchQueryKey.SelectedActivity) || undefined;
};

export const getEndDateFromQueryParams = (queryString: string): Date | undefined => {
    const queryEndDate = new QueryString(queryString).get(SearchQueryKey.EndDate);
    if (queryEndDate) {
        const date = parseISO(queryEndDate);
        return isValid(date) && (!isPast(date) || isToday(date)) ? date : undefined;
    }
    return undefined;
};

export const mapActivityForDetail = (detailState?: DetailsState): ActivityWithContent => {
    const result = (detailState && detailState.detailsResponse && detailState.detailsResponse.result) || (EmptyObject as DetailsActivityResult);
    return (result && result.activity) || EmptyObject;
};

export const mapActivityContentForDetail = (detailState?: DetailsState): Content => {
    const result = mapActivityForDetail(detailState);
    return result.content || EmptyObject;
};

export const mapDetailByActivityWithContent = (activityWithContent?: ActivityWithContent): Detail => {
    return (activityWithContent?.content && activityWithContent.content.detail) || EmptyObject;
};

export const mapContentByActivityWithContent = (activityWithContent?: ActivityWithContent): Content => {
    return (activityWithContent && activityWithContent?.content) || EmptyObject;
};

export const mapActivityDetail = (detailState?: DetailsState): Detail => {
    const result = mapActivityContentForDetail(detailState);
    return result.detail || EmptyObject;
};

export const mapActivityDetailByDetailResult = (result?: DetailsActivityResult): Detail => {
    return result?.activity?.content?.detail || EmptyObject;
};

export const mapActivityLocation = (detailState?: DetailsState): Location[] => {
    const detail = mapActivityDetail(detailState);
    return (detail && detail.locations) || [];
};

export const mapLogisticsByDetail = (detail: Detail): Logistic => {
    return (detail && detail.logistics && detail.logistics.length > 0 && detail.logistics[0]) || EmptyObject;
};

export const mapTicketsByDetail = (detail: Detail) => {
    return detail && detail.ticketing && detail.ticketing.ticketTypes;
};

export const mapLocationsByDetail = (detail: Detail) => {
    return (detail && detail.locations && detail.locations.length > 0 && detail.locations) || [];
};

export const mapItineraryByDetail = (detail: Detail) => {
    return detail && detail.itineraries && detail.itineraries.length > 0 ? detail.itineraries[0] : undefined;
};

export const mapItinerary = (state: DetailsState) => {
    const detail = mapActivityDetail(state);
    return mapItineraryByDetail(detail);
};

// @requirementId 0 is the first valid booking requirement
export const findBookingRequirementById = (bookingRequirements?: BookingRequirement[], requirementId = 0): BookingRequirement => {
    if (!bookingRequirements || bookingRequirements.length === 0) return EmptyObject;
    // Details response, BookingRequirement will not have the id. bookingRequirement.id => undefined
    // Calendar response, BookingRequirement will have the id. bookingRequirement.id => -1
    const hasBookingRequirementId = bookingRequirements[0].id !== undefined;
    if (hasBookingRequirementId) {
        return bookingRequirements.find((requirement) => requirement && requirement.id === requirementId) || EmptyObject;
    }
    return getElementAtIndex(bookingRequirements, requirementId) || EmptyObject;
};

export const mapDescription = (description?: Description, joiner = ', '): string => {
    return description && description.section && description.section.length > 0 && description.section[0].descriptions
        ? description.section[0].descriptions.join(joiner)
        : EmptyValue;
};

export const mapTitle = (description?: Description): string => {
    return description && description.section && description.section.length > 0 && description.section[0].title ? description.section[0].title : EmptyValue;
};

export const mapPriceFilter = (rangeFilter?: RangeFilterMatrix[]): RangeFilterMatrix => {
    if (!rangeFilter || rangeFilter.length === 0) {
        return EmptyObject;
    }

    return rangeFilter.find((r) => r.code === FilterCode.Price) || EmptyObject;
};

export const mapRatingFilter = (filterMatrix?: RangeFilterMatrix[]): RangeFilterMatrix => {
    if (!filterMatrix?.length) {
        return EmptyObject;
    }
    const rating = filterMatrix.find((r) => r.code === FilterCode.Rating);
    if (rating) {
        const bucket = rating.bucket || [];
        const sortedBucket = bucket
            .filter((b) => b.min !== undefined)
            .slice()
            .sort((a, b) => (b.min || 0) - (a.min || 0));
        return {
            ...rating,
            bucket: sortedBucket,
        };
    }
    return filterMatrix.find((r) => r.code === FilterCode.Rating) || EmptyObject;
};

export const mapValueFilter = (code: FilterCode, filterMatrix?: ValueFilterMatrix[]): ValueFilterMatrix => {
    if (!filterMatrix?.length) {
        return EmptyObject;
    }

    return filterMatrix.find((r) => r.code === code) || EmptyObject;
};

export const mapCategoryFilter = (filterMatrix?: ValueFilterMatrix[]): ValueFilterMatrix => {
    if (!filterMatrix?.length) {
        return EmptyObject;
    }

    return filterMatrix.find((r) => r.code === FilterCode.Category) || EmptyObject;
};

export const mapSubcategoryFilter = (filterMatrix?: ValueFilterMatrix[]): ValueFilterMatrix => {
    if (!filterMatrix?.length) {
        return EmptyObject;
    }

    return filterMatrix.find((r) => r.code === FilterCode.SubCategory) || EmptyObject;
};

export const mapTitleFromContent = (content: Content = {}): string => {
    const offerDetail = content.detail?.offerDetails?.[0] ?? {};
    const providerName = offerDetail.supplierInfo?.providerName || '';
    const title = content.activity?.title || '';
    return providerName ? `${title} - ${providerName}` : title;
};

export const mapDescriptionFromContent = (content: Content = {}): string => {
    return content.activity?.description || '';
};

export const hasInstantConfirmationBadge = (content: Content = {}): boolean => {
    return content.badges?.keyAttributes?.some((badge) => badge.badgeType === BadgeType.INSTANT_CONFIRMATION) ?? false;
};
